'use client';

import { Select } from '@/_components';
import type { QuestionFormSelectProps } from './QuestionFormSelect.types';
import { useContext, useState, useMemo, type FC, useCallback } from 'react';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import styles from './QuestionFormSelect.module.css';

export const QuestionFormSelect: FC<QuestionFormSelectProps> = ({
  question,
  isDisabled,
  onChange,
  mode,
}) => {
  const { updateQuestion } = useContext(ClientQuestionnaireContext);
  const { updatePreviewQuestion } = useContext(PreviewContext);
  const { updateTemplateQuestion } = useContext(UserPreviewContext);

  const [isLoading, setLoading] = useState<boolean>(false);

  const answerQuestion = useCallback(
    async (answerParams: { questionId: number; answer: string }) => {
      setLoading(true);

      try {
        if (mode === 'admin-preview') {
          await updatePreviewQuestion({ ...answerParams });
        } else if (mode === 'admin-template') {
          await updateTemplateQuestion(answerParams);
        } else {
          await updateQuestion({ ...answerParams, isAsync: true });
        }
      } finally {
        setLoading(false);
      }
    },
    [mode, updateQuestion, updateTemplateQuestion, updatePreviewQuestion],
  );

  const onAnswer = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const answerParams = {
      questionId: question.id,
      answer: event.target.value,
    };

    onChange?.(event.target.value);
    await answerQuestion(answerParams);
  };

  const selectItems = useMemo(() => {
    return (
      question.options?.map((option: string) => ({
        name: option,
        value: option,
      })) || []
    );
  }, [question.options]);

  return (
    <Select
      label="Answer"
      isLabelHidden={true}
      isDisabled={isDisabled || isLoading}
      items={selectItems}
      selectedValue={question.answer || ''}
      onChange={onAnswer}
      placeholder="Select an option"
      className={styles.select}
    />
  );
};
